const VIEWED_PRODUCTS_KEY = 'viewedProducts';
const PRODUCT_HANDLES_KEY = 'celiraeViewedProductHandles';

function readJson(key, fallback) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '');
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
}

function normalizeProductIds(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === 'string' || typeof item === 'number') return String(item);
      if (item && typeof item === 'object' && item.id) return String(item.id);
      return '';
    })
    .filter(Boolean);
}

function formatPrice(cents) {
  const amount = Number(cents || 0) / 100;
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

function imageUrl(source) {
  if (!source) return '';

  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '720');
    return url.toString();
  } catch {
    return source;
  }
}

function productUrl(product) {
  const root = window.Shopify?.routes?.root || '/';
  return product.url || `${root}products/${encodeURIComponent(product.handle)}`;
}

function createCardAction(product) {
  const action = document.createElement('div');
  action.className = 'celirae-card-action';
  action.dataset.celiraeProductId = String(product.id || '');

  const add = document.createElement('div');
  add.className = 'celirae-card-action__add';
  add.dataset.celiraeAdd = '';

  if (product.available) {
    const addLink = document.createElement('a');
    addLink.className = 'celirae-card-action__button';
    addLink.href = productUrl(product);
    addLink.textContent = 'ADD TO CART';
    add.append(addLink);
  } else {
    const soldOut = document.createElement('span');
    soldOut.className = 'celirae-card-action__sold-out';
    soldOut.setAttribute('aria-disabled', 'true');
    soldOut.textContent = 'SOLD OUT';
    add.append(soldOut);
  }

  const root = window.Shopify?.routes?.root || '/';
  const view = document.createElement('a');
  view.className = 'celirae-card-action__view';
  view.dataset.celiraeView = '';
  view.href = `${root}cart`;
  view.textContent = 'VIEW CART';
  view.hidden = true;

  action.append(add, view);
  return action;
}

async function fetchProduct(handle) {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function createProductCard(product) {
  const article = document.createElement('article');
  article.className = 'celirae-recently-viewed__card';

  const link = document.createElement('a');
  link.className = 'celirae-recently-viewed__link';
  link.href = productUrl(product);
  link.setAttribute('aria-label', product.title || 'View product');

  const imageWrap = document.createElement('div');
  imageWrap.className = 'celirae-recently-viewed__image-wrap';

  const imageSource = product.featured_image || product.images?.[0];
  if (imageSource) {
    const image = document.createElement('img');
    image.className = 'celirae-recently-viewed__image';
    image.src = imageUrl(imageSource);
    image.alt = product.title || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    imageWrap.append(image);
  }

  const title = document.createElement('h3');
  title.className = 'celirae-recently-viewed__title';
  title.textContent = product.title || '';

  const price = document.createElement('p');
  price.className = 'celirae-recently-viewed__price';
  price.textContent = formatPrice(product.price_min ?? product.price);

  link.append(imageWrap, title, price);
  article.append(link, createCardAction(product));
  return article;
}

class CeliraeRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const currentId = String(this.dataset.currentProductId || '');
    const currentHandle = String(this.dataset.currentProductHandle || '');
    const maxProducts = Math.max(1, Number(this.dataset.maxProducts || 4));

    const handleMap = readJson(PRODUCT_HANDLES_KEY, {});
    if (currentId && currentHandle) {
      handleMap[currentId] = currentHandle;
      writeJson(PRODUCT_HANDLES_KEY, handleMap);
    }

    const viewedIds = normalizeProductIds(readJson(VIEWED_PRODUCTS_KEY, []));
    const handles = [
      ...new Set(
        viewedIds
          .filter((id) => id !== currentId)
          .map((id) => handleMap[id])
          .filter(Boolean)
      ),
    ].slice(0, maxProducts);

    if (!handles.length) {
      this.hidden = true;
      return;
    }

    const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
    if (!products.length) {
      this.hidden = true;
      return;
    }

    const grid = this.querySelector('[data-recently-viewed-grid]');
    if (!grid) {
      this.hidden = true;
      return;
    }

    grid.replaceChildren(...products.map(createProductCard));
    this.hidden = false;
  }
}

if (!customElements.get('celirae-recently-viewed')) {
  customElements.define('celirae-recently-viewed', CeliraeRecentlyViewed);
}
